import moment from 'moment';

function canRepublish(experience) {
  return (
    isRollingRetentionStrategy(experience) ||
    !experience.retentionDate || // allow to republish if retentionDate has been erased in old campaigns
    moment().isBefore(experience.retentionDate)
  );
}

/**
 * Returns the max expiry date that the user can set
 * If the experience has already established an expiry based retention strategy, then return the max allowed extension date
 * If the experience has a publish date, then the expiration date can be no more than 1 year after
 * If the experience does NOT have a publish date, then we get the max publish date and apply the same 1 year later rule
 * @param {Experience} experience
 */
function getMaxExpiryDate(experience) {
  const _publishDate = experience.publishDate ? experience.publishDate : getMaxPublishDate(experience);
  return isExpiryBasedRetentionStrategy(experience)
    ? experience.maxAllowedExtensionDate
    : moment(_publishDate).add(1, 'years').subtract(1, 'days').toDate();
}

/**
 * Returns the max publish date that the user can set
 * If the experience has already established an expiry based retention strategy, then return the max allowed extension date
 * If the experience already has an expiration date set, then naturally you cannot publish it after the expiration date, so we use that
 * If the experience does NOT have an expiration date set, then we restrict them to 1 year.
 * @param {Experience} experience
 */
function getMaxPublishDate(experience, originalExpiryDate) {
  const aYearLater = moment().add(1, 'years').toDate();

  if (experience.isExpired) {
    return moment(originalExpiryDate || experience.expiryDate)
      .add(App.globalContext.maxDaysToRepublish, 'days')
      .toDate();
  } else if (isExpiryBasedRetentionStrategy(experience)) {
    return experience.maxAllowedExtensionDate;
  } else if (isRollingRetentionStrategy(experience)) {
    return aYearLater;
  } else if (!experience.expiryDate) {
    return aYearLater;
  } else {
    return new Date(Math.min(experience.expiryDate, aYearLater));
  }
}

/**
 * An alias for maxExtensionDate because it is often referred to this in tickets
 * This is the date when date will start being retained for less than the entire retention period
 * @param {Experience} experience
 */
function getWarningStartDate(experience) {
  return isExpiryBasedRetentionStrategy(experience)
    ? moment(experience.maxExtensionDate).add(1, 'days').toDate()
    : undefined;
}

/**
 * An alias for maxAllowedExtensionDate because it is often refered to this in tickets
 * This is the date when the user will no longer be able to change the experience and impact the data as all data is about to get removed
 * @param {Experience} experience
 */
function getWarningEndDate(experience) {
  return experience.maxAllowedExtensionDate;
}

/**
 * Returns true when the experience has a retention strategy set
 * Definition: A retention strategy is set the first time an experience is published
 * @param {Experience} experience
 */
function hasRetentionStrategy(experience) {
  return experience.storagePartition !== null;
}

/**
 * Returns true if the customer still has access to their data
 * @param {Experience} experience
 */
function isDataStillAvailable(experience) {
  return !isExpiryBasedRetentionStrategy(experience) || moment().isBefore(experience.retentionDate);
}

/**
 * [V]:No longer exported, used here locally ONLY. There is a better way to get this value now in the experience model.
 * Might be worth replacing it where it is used when we get there.
 *
 *
 * Returns true if the experience is an expiration based retention strategy
 * Returns false if the experience is a rolling retention strategy or if the retention strategy has yet to be determined
 * Definition: Expiry based retention means that data will be retained until the experience expires + the duration of the retention period.
 *  - This could be the expiry date set when the experience is published
 *  - Or it could be the date that the user manually expires the experience
 *  - Note: The retention date is not the same as the removal date
 * @param {Experience} experience
 */
function isExpiryBasedRetentionStrategy(experience) {
  return experience.storagePartition !== 0 && experience.storagePartition !== null;
}

/**
 * Returns true if the experience is in rolling retention strategy
 * Returns false if the experience is an expiration based retention strategy or if the retention strategy has yet to be determined
 * Definition: Rolling retention means that data will be retained from the date the data is received + the duration of the retention period.
 *  - Note: Expiring a campaign does not change the retention strategy
 * @param {Experience} experience
 */
function isRollingRetentionStrategy(experience) {
  return experience.storagePartition === 0;
}

/**
 * Returns true if the expiryDate occurs between the (removal date - 7 days) and the (removal date - retention period). i.e. (removalDate - storageRetentionPeriod >= expiryDate && expiryDate <= rentiodPeriod - 7)
 * Definition: The user can still choose an expiration date in the warning period, but the data will not be retained for the entirety of the retention period. We typically want to alert the user to this.
 *  - On the experience, this is the maxExtensionDate property
 *  - Note: The 7 day "grace" period that we subtract from the removal date is to prevent any complications in the BE. i.e. we don't allow the user to set an expiry date within 7 days of the removal date
 * @param {Experience} experience
 * @param {Date} date - a date to check if it's in the warning period
 */
function isInWarningPeriod(experience, date) {
  return (
    isExpiryBasedRetentionStrategy(experience) &&
    moment(date).isSameOrAfter(getWarningStartDate(experience)) &&
    moment(date).isSameOrBefore(getWarningEndDate(experience))
  );
}

export default {
  canRepublish,
  getMaxExpiryDate,
  getMaxPublishDate,
  getWarningStartDate,
  getWarningEndDate,
  hasRetentionStrategy,
  isDataStillAvailable,
  isRollingRetentionStrategy,
  isInWarningPeriod,
};
